import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import type { SplashListLinkProps } from '../../../../../styled-components/components/SplashList';
import type { PageHeaderBreadcrumbsProps } from '../../../../../styled-components/structures/PageHeader';
import {
  type NavigationPage,
  getBreadcrumbs,
  getNavigationMenu,
  getNavigationTree,
  getQuickNavigation,
} from './utils';

interface MainNavigationContextProps {
  initialized: boolean;
  navigationTree: NavigationPage[];
  quickNavigationLinks: NavigationPage[];
  menuLinks?: NavigationPage[];
  activePage: NavigationPage;
  breadcrumbs: PageHeaderBreadcrumbsProps[];
  landingPageLinks?: SplashListLinkProps[];
  expanded: boolean;
  mobileMenu: boolean;
  onOpen: () => void;
  onClose: () => void;
  onToggleMobileMenu: () => void;
}

const MainNavigationContext = createContext<MainNavigationContextProps | undefined>(undefined);

interface MainNavigationProviderProps {
  children: React.ReactNode;
}

export const MainNavigationProvider = ({ children }: MainNavigationProviderProps) => {
  const { pathname } = useLocation();

  const [expanded, setExpanded] = useState(true);
  const onOpen = useCallback(() => setExpanded(true), []);
  const onClose = useCallback(() => setExpanded(false), []);

  const [mobileMenu, setMobileMenu] = useState(false);
  const onToggleMobileMenu = useCallback(() => setMobileMenu((s) => !s), []);

  const navigationTree = useMemo(() => getNavigationTree(pathname), [pathname]);

  const quickNavigationLinks = useMemo(() => getQuickNavigation(navigationTree), [navigationTree]);

  const baseRoute = pathname.split('/').slice(0, 2).join('/');

  const menuLinks = useMemo(
    () => getNavigationMenu(baseRoute, navigationTree),
    [baseRoute, navigationTree],
  );

  const fullBreadcrumbs = useMemo(() => getBreadcrumbs(navigationTree), [navigationTree]);

  const breadcrumbs = useMemo(
    () =>
      fullBreadcrumbs.map((page) => ({
        displayName: page.title,
        link: { title: page.title, to: page.url },
      })),
    [fullBreadcrumbs],
  );

  const activePage = useMemo(
    () => fullBreadcrumbs[fullBreadcrumbs.length - 1] || {},
    [fullBreadcrumbs],
  );

  const landingPageLinks = useMemo(
    () =>
      activePage.pages?.map((page) => ({
        to: page.url,
        heading: page.title,
        description: page.description,
        icon: page.icon,
        externalUrl: page.externalUrl,
      })),
    [activePage],
  );

  return (
    <MainNavigationContext.Provider
      value={{
        initialized: true,
        navigationTree,
        quickNavigationLinks,
        menuLinks,
        breadcrumbs,
        activePage,
        landingPageLinks,
        expanded,
        mobileMenu,
        onOpen,
        onClose,
        onToggleMobileMenu,
      }}
    >
      {children}
    </MainNavigationContext.Provider>
  );
};

const useMainNavigation = (): MainNavigationContextProps => {
  const context = useContext(MainNavigationContext);

  if (!context) {
    throw new Error('useMainNavigation must be used within a MainNavigationContext');
  }

  return context;
};

export const useMobileMenuNavigation = () => {
  const { mobileMenu, onToggleMobileMenu } = useMainNavigation();
  return useMemo(
    () => ({
      mobileMenu,
      onToggleMobileMenu,
    }),
    [onToggleMobileMenu, mobileMenu],
  );
};

export const useNavigationTree = () => {
  const { navigationTree } = useMainNavigation();
  return useMemo(() => navigationTree, [navigationTree]);
};

export const useMainNavigationInitialized = () => {
  const { initialized } = useMainNavigation();
  return useMemo(() => initialized, [initialized]);
};

export const useQuickNavigationLinks = () => {
  const { quickNavigationLinks } = useMainNavigation();
  return useMemo(() => quickNavigationLinks, [quickNavigationLinks]);
};

export const useMenuLinks = () => {
  const { menuLinks } = useMainNavigation();
  return useMemo(() => menuLinks, [menuLinks]);
};

export const useBreadcrumbs = (includeLinkOfLast = false) => {
  const { breadcrumbs } = useMainNavigation();
  return useMemo(() => {
    const length = breadcrumbs.length;
    return [...breadcrumbs].map((b, i) => ({
      ...b,
      link: includeLinkOfLast || length - 1 !== i ? b.link : undefined,
    }));
  }, [breadcrumbs, includeLinkOfLast]);
};

export const useActivePage = () => {
  const { activePage } = useMainNavigation();
  return useMemo(() => activePage, [activePage]);
};

export const useLandingPageLinks = () => {
  const { landingPageLinks } = useMainNavigation();
  return useMemo(() => landingPageLinks, [landingPageLinks]);
};

export default useMainNavigation;
